/**
 * 画面の IF（作り直し）— 一覧（`listScreens`）と詳細（`getScreenDetail`）。
 *
 * 読むのは作り直しの表（`spec_graph_*`）で、ID は number である。
 *
 * **ID はすべて number である**（決定 2）。画面の id も部品の id も案件番号も number で、
 * 画面が `AIRCLOSET-` を前に付けて 6 桁で出す。
 *
 * **名前が空でも行を落とさない。** 本番の画面 95 件は名前の材料（`subject` / `verb`）が
 * 未設定のまま復元されている。空を落とすと一覧が 0 件になるので、空文字のまま返して
 * 「未記入」を表示層が添える（`displayLabel` / `SpecLabelBadges`）。**未翻訳を原文で
 * 埋めない**（決定 4）のと同じ理由で、読取が既定の名前を作ることもしない。
 *
 * 欄の正は設計書 API §4.4 で、画面の種類・部品の画面入出力項目・アクション・入力チェックの
 * 選択肢は共通部の契約から引く（入口に写さない）。値が無い欄は `null` か空の並びで返す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の一覧と画面 1 枚の詳細（作り直し）の入出力スキーマ。種類・導出（ドメイン・属するステージ・見るアクター）・部品の画面入出力項目とアクションと入力チェック・遷移元・操作する業務への入口を持つ形
 * @graph-connects none
 */
import { COMPONENT_KINDS, COMPONENT_VALIDATION_KINDS, SCREEN_KINDS, SCREEN_TRANSITION_CONDITIONS, TERM_REPRESENTATIONS, } from '@cortex/domain-spec-graph';
import { z } from 'zod';
import { LocaleInputShape, UntranslatedSchema } from '../spec-graph-common.js';
import { ElementPjSideSchema } from './user-story-flow.js';
/**
 * 作り直しの要素 1 件を指す番号。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の読取が受け渡す要素 1 件の番号（1 以上の整数）
 * @graph-connects none
 */
export const ScreenIdSchema = z.number().int().positive();
/**
 * `listScreens` の入力。案件と表示言語だけを受ける。
 *
 * 絞り込みを持たないのは、名前と URL パターンの絞り込みが**画面の中の操作**だからである。
 * 95 件は全件返して構わない規模と決まっているので、入力するたびにサーバー往復を増やさない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `listScreens` の入力（案件・表示言語）
 * @graph-connects none
 */
export const ScreenListInputSchema = z.object({
    pj: ElementPjSideSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 画面の読取に載る参照 1 件（表示条件の判定表・遷移先の画面・操作する業務・見るアクター）。
 *
 * 未翻訳の旗を持たない。行の旗は「この行はこの言語で完結して読めるか」を答えるもので、
 * 参照先の表示名は行そのものの本文ではないためである（`getBusinessMap` と同じ扱い）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の読取に載る参照 1 件（id と表示名）
 * @graph-connects none
 */
export const ScreenRefSchema = z.object({
    id: ScreenIdSchema,
    label: z.string(),
});
/**
 * 画面のドメイン（配信するホスト）。ノード型の Domain（業務の束ね）とは別物である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面を配信するホスト（ホスト名と表示名）
 * @graph-connects none
 */
export const ScreenDomainSchema = z.object({
    host: z.string(),
    label: z.string(),
});
/**
 * 画面の行と詳細が共通で持つ、画面の種類と導出（ドメイン・属するステージ・見るアクター）。
 *
 * 導出は共通部の関数で読むたびに導く。`stageIds` が空の画面は「ステージ未定」、`domain` が
 * `null` の画面はホストが決まらない画面である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面の行と詳細が共通で持つ種類・ドメイン・属するステージ・見るアクター
 * @graph-connects none
 */
const ScreenPlacementShape = {
    kind: z.enum(SCREEN_KINDS).nullable(),
    domain: ScreenDomainSchema.nullable(),
    stageIds: z.array(ScreenIdSchema),
    accessActors: z.array(ScreenRefSchema),
};
/**
 * 画面一覧の 1 行。
 *
 * `pathPattern` が `null` になるのは欄そのものが無い行である。**既定のパスで埋めない** —
 * `/` を置くと、パスの決まっていない画面が根のパスを持つ画面として並ぶ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面一覧の 1 行（id・表示名・未翻訳・URL パターン・部品数・種類・ドメイン・属するステージ・見るアクター）
 * @graph-connects none
 */
export const ScreenListItemSchema = z.object({
    screenId: ScreenIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    pathPattern: z.string().nullable(),
    componentCount: z.number().int().nonnegative(),
    ...ScreenPlacementShape,
});
/**
 * 画面一覧が画面を分けるステージ 1 件。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面一覧が画面を分けるステージ 1 件（id・表示名・未翻訳）
 * @graph-connects none
 */
export const ScreenListStageSchema = z.object({
    stageId: ScreenIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/**
 * `listScreens` の出力。
 *
 * ページングしない。件数（95）は 1 画面に並べて絞り込む規模であり、**絞り込みが画面の中の
 * 操作**なので、続きを取りに戻る形にすると押すたびに往復が要る。
 *
 * 画面は遷移の順、ステージは鎖の順で返す。絞り込みの選択肢（ステージ・ドメイン）も読取が返し、
 * 画面の側に値を写さない。`stages` と `domains` を省略可にしているのは、API より先に画面が
 * デプロイされた間も画面が落ちないようにするためである（読取の形を足す中間）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `listScreens` の出力（遷移の順の画面の行・鎖の順のステージ・選べるドメイン）
 * @graph-connects none
 */
export const ScreenListOutputSchema = z.object({
    screens: z.array(ScreenListItemSchema),
    stages: z.array(ScreenListStageSchema).optional(),
    domains: z.array(ScreenDomainSchema).optional(),
});
/**
 * `getScreenDetail` の入力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getScreenDetail` の入力（画面の番号・案件・表示言語）
 * @graph-connects none
 */
export const ScreenDetailInputSchema = z.object({
    screenId: ScreenIdSchema,
    pj: ElementPjSideSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 部品が操作する先 1 件。業務と処理のどちらも指しうる。
 *
 * **型を添えるのは入口を出せるかが型で決まるため**である。業務には `/businesses/$id` と
 * `/businesses/$id/logic` の 2 つの入口があるが、処理にはその画面が無い。型を落とすと、
 * 表示層が在りもしない画面へのリンクを出すか、業務への入口も全部やめるかのどちらかになる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 部品が操作する先 1 件（id・表示名・業務か処理かの型）
 * @graph-connects none
 */
export const ScreenOperatesSchema = ScreenRefSchema.extend({
    nodeType: z.enum(['Business', 'Rule']),
});
/**
 * コンポーネントの変数部の表示ロジック 1 件（名前の材料の用語）。画面仕様書の「初期表示」「変数部の表示ロジック」に
 * 当たる欄で、文言そのものは仕様グラフが持たないので、用語の名前と定義で答える。定義は表示言語 1 つへ
 * 畳み、無ければ `null`。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business コンポーネントの変数部の表示ロジック 1 件（用語の id・表示名・定義）
 * @graph-connects none
 */
export const ScreenSubjectSchema = ScreenRefSchema.extend({
    description: z.string().nullable(),
});
/**
 * 部品の画面入出力項目。型は用語（プロパティ）への参照か表現の型、初期値は値の用語への参照か
 * 数値。書かれていない欄は `null` である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 部品の画面入出力項目（型・桁・必須・初期値）
 * @graph-connects none
 */
export const ScreenIoSchema = z.object({
    dataType: z
        .union([
        z.object({ term: ScreenRefSchema }),
        z.object({ representation: z.enum(TERM_REPRESENTATIONS) }),
    ])
        .nullable(),
    length: z.number().int().positive().nullable(),
    required: z.boolean().nullable(),
    initialValue: z
        .union([z.object({ term: ScreenRefSchema }), z.object({ literal: z.number() })])
        .nullable(),
});
/**
 * 部品の中の番号（アクション・入力チェック）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 部品の中で 1 から振るアクション・入力チェックの番号
 * @graph-connects none
 */
const ComponentLocalIdSchema = z.number().int().positive();
/**
 * 部品のアクション 1 つ。本文（きっかけ・メッセージ）は表示言語 1 つへ畳み、値が無ければ `null`。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 部品のアクション 1 つ（部品の中の番号・きっかけ・遷移先・異常時の遷移先・メッセージ）
 * @graph-connects none
 */
export const ScreenActionSchema = z.object({
    id: ComponentLocalIdSchema,
    trigger: z.string().nullable(),
    transitionTo: ScreenRefSchema.nullable(),
    onErrorTransitionTo: ScreenRefSchema.nullable(),
    message: z.string().nullable(),
});
/**
 * 部品の入力チェック 1 つ。本文（規則・メッセージ）は表示言語 1 つへ畳み、値が無ければ `null`。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 部品の入力チェック 1 つ（部品の中の番号・規則・種別・メッセージ）
 * @graph-connects none
 */
export const ScreenValidationSchema = z.object({
    id: ComponentLocalIdSchema,
    rule: z.string().nullable(),
    kind: z.enum(COMPONENT_VALIDATION_KINDS).nullable(),
    message: z.string().nullable(),
});
/**
 * 詳細に並ぶ部品 1 件。
 *
 * `kind` / `visibleWhen` が `null` になるのは、骨格が部品を立てただけで詳細の入力がまだ
 * 埋まっていない行である（`kind` は契約の種別に無い値も `null`）。**既定の種別で埋めない** —
 * ボタンで埋めると、種別の決まっていない部品がボタンとして画面に並ぶ。
 *
 * `transitionTo` は部品の遷移先の欄から来た遷移だけで、アクションの遷移先は `actions[]` に載る。
 *
 * `subject` を省略可にしているのは、API より先に画面がデプロイされた間も画面が落ちないように
 * するためである（読取の形を足す中間。`ScreenListOutputSchema` の `stages` と同じ）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面詳細に並ぶ部品 1 件（id・表示名・未翻訳・種別・変数部の表示ロジック・操作する先・表示条件・遷移先・画面入出力項目・アクション・入力チェック）
 * @graph-connects none
 */
export const ScreenComponentRowSchema = z.object({
    componentId: ScreenIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    kind: z.enum(COMPONENT_KINDS).nullable(),
    subject: ScreenSubjectSchema.nullable().optional(),
    operates: ScreenOperatesSchema.nullable(),
    visibleWhen: ScreenRefSchema.nullable(),
    transitionTo: z.array(ScreenRefSchema),
    io: ScreenIoSchema.nullable(),
    actions: z.array(ScreenActionSchema),
    validations: z.array(ScreenValidationSchema),
});
/**
 * 遷移を起こすアクション（辺のイベント）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面へ流入する遷移を起こすアクション（部品の中の番号ときっかけ）
 * @graph-connects none
 */
export const ScreenTransitionEventSchema = z.object({
    actionId: ComponentLocalIdSchema,
    trigger: z.string().nullable(),
});
/**
 * この画面へ遷移してくる経路 1 本。
 *
 * **遷移元の画面と、その画面のどの部品から来るかを両方持つ。** 画面だけを出すと、同じ画面から
 * 2 本入ってくるときに 1 行に畳まれ、どちらの部品を直せばよいかが画面から消える。
 *
 * `screenId` が `null` になるのは、遷移を起こす部品がどの画面にも属していない場合である。
 * **0 で埋めない** — 0 を置くと「画面 0 から来る」と読める。
 *
 * `event` と `condition` は辺の属性で、部品の遷移先の欄から来た辺はどちらも `null` である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面へ流入する経路 1 本（遷移元の画面と、遷移を起こす部品・アクション・条件）
 * @graph-connects none
 */
export const ScreenIncomingSchema = z.object({
    screenId: ScreenIdSchema.nullable(),
    screenLabel: z.string(),
    componentId: ScreenIdSchema,
    componentLabel: z.string(),
    event: ScreenTransitionEventSchema.nullable(),
    condition: z.enum(SCREEN_TRANSITION_CONDITIONS).nullable(),
});
/**
 * Figma のフレームへの参照。どちらも Figma の側の文字列である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 画面が指す Figma のフレーム（ファイルの鍵とノード ID）
 * @graph-connects none
 */
export const ScreenFigmaSchema = z.object({
    fileKey: z.string(),
    nodeId: z.string(),
});
/**
 * `getScreenDetail` の出力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business `getScreenDetail` の出力（画面の表示名・URL パターン・種類・束ね・Figma の参照・導出・部品・遷移元・操作する業務への入口）
 * @graph-connects none
 */
export const ScreenDetailOutputSchema = z.object({
    screenId: ScreenIdSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    pathPattern: z.string().nullable(),
    ...ScreenPlacementShape,
    bundle: z.string().nullable(),
    figma: ScreenFigmaSchema.nullable(),
    components: z.array(ScreenComponentRowSchema),
    incoming: z.array(ScreenIncomingSchema),
    operatedBusinesses: z.array(ScreenRefSchema),
});
//# sourceMappingURL=screens.js.map